"use client";

import { ChevronRight, ChevronDown, Edit, Trash2 } from "lucide-react";
import { TreeItem, TreeConfig } from "../types";

interface TreeNodeProps {
  item: TreeItem;
  level: number;
  treeConfig: TreeConfig;
  expandedIds: Set<string>;
  editingItemId: string | null;
  onToggleExpand: (id: string) => void;
  onEdit: (item: TreeItem) => void;
  onDelete: (item: TreeItem) => void;
}

export const TreeNode = ({
  item,
  level,
  treeConfig,
  expandedIds,
  editingItemId,
  onToggleExpand,
  onEdit,
  onDelete,
}: TreeNodeProps) => {
  const idField = treeConfig.fieldMappings.id;
  const childrenField = treeConfig.itemDisplayFields.childrenField || "children";
  const primaryField = treeConfig.itemDisplayFields.primary || "title";
  const secondaryField = treeConfig.itemDisplayFields.secondary;
  const statusField = treeConfig.itemDisplayFields.statusField;
  const statusInactiveLabel = treeConfig.itemDisplayFields.statusInactiveLabel || "Inactive";

  const hasChildren = item[childrenField] && item[childrenField].length > 0;
  const isExpanded = expandedIds.has(String(item[idField]));
  const isEditing = editingItemId === String(item[idField]);

  return (
    <div key={item[idField]}>
      <div
        className={`flex items-center gap-2 px-2 py-1.5 rounded group transition-colors ${
          isEditing ? "bg-blue-50 border border-blue-300" : "hover:bg-gray-50"
        }`}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
      >
        {hasChildren ? (
          <button
            onClick={() => onToggleExpand(String(item[idField]))}
            className="w-4 h-4 flex items-center justify-center hover:bg-gray-200 rounded flex-shrink-0"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <div className="w-4 h-4 flex-shrink-0" />
        )}

        <div className="flex-1 flex items-center gap-2 min-w-0">
          <span className="font-medium text-gray-900 truncate">{item[primaryField] || ""}</span>
          {secondaryField && (
            <span className="text-xs text-gray-400 truncate">/{item[secondaryField] || ""}</span>
          )}
          {statusField && !item[statusField] && (
            <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded flex-shrink-0">
              {statusInactiveLabel}
            </span>
          )}
        </div>

        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={() => onEdit(item)}
            className="p-1 hover:bg-blue-100 rounded text-blue-600"
            title="Edit"
          >
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-1 hover:bg-red-100 rounded text-red-600"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="mt-0.5">
          {item[childrenField].map((child: TreeItem) => (
            <TreeNode
              key={child[idField]}
              item={child}
              level={level + 1}
              treeConfig={treeConfig}
              expandedIds={expandedIds}
              editingItemId={editingItemId}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

